// #tag: OPPORTUNITIES API CLIENT
// #purpose: Dispatches API requests for paginated career opportunities, urgency filters, taxonomy metadata, and candidate tracking toggles.
import { apiDelete, apiGet, apiPost } from './client';
import type { Opportunity, OpportunityMeta, TrackedOpportunity } from '../types';

export interface PaginatedResponse<T> {
  data: T[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
  };
}

export interface OpportunitiesQuery {
  q?: string;
  category?: string;
  opportunityType?: string;
  qualification?: string;
  state?: string;
  status?: string;
  window?: 'new_today' | 'this_week' | 'closing_soon' | 'open' | 'recently_closed';
  page?: number;
  limit?: number;
  sort?: string;
  order?: 'ASC' | 'DESC';
}

function toQuery(params: Record<string, string | number | boolean | undefined>): string {
  const sp = new URLSearchParams();
  Object.entries(params).forEach(([key, value]) => {
    if (value !== undefined && value !== '' && value !== 'all') sp.set(key, String(value));
  });
  const qs = sp.toString();
  return qs ? `?${qs}` : '';
}

export function fetchOpportunities(query: OpportunitiesQuery = {}): Promise<PaginatedResponse<Opportunity>> {
  return apiGet(`/opportunities${toQuery(query as Record<string, string | number | boolean | undefined>)}`);
}

export function fetchLatestOpportunities(limit = 6): Promise<{ data: Opportunity[] }> {
  return apiGet(`/opportunities/latest?limit=${limit}`);
}

export function fetchFeaturedOpportunities(limit = 4): Promise<{ data: Opportunity[] }> {
  return apiGet(`/opportunities/featured?limit=${limit}`);
}

export function fetchTrendingOpportunities(limit = 6): Promise<{ data: Opportunity[] }> {
  return apiGet(`/opportunities/trending?limit=${limit}`);
}

export function fetchClosingSoonOpportunities(limit = 6): Promise<{ data: Opportunity[] }> {
  return apiGet(`/opportunities/closing-soon?limit=${limit}`);
}

export function fetchRecentlyClosedOpportunities(limit = 6): Promise<{ data: Opportunity[] }> {
  return apiGet(`/opportunities/recently-closed?limit=${limit}`);
}

export function fetchOpportunityMeta(): Promise<OpportunityMeta> {
  return apiGet('/opportunities/meta');
}

export function fetchOpportunityBySlug(slug: string): Promise<Opportunity> {
  return apiGet(`/opportunities/${slug}`);
}

export function fetchTrackedOpportunities(): Promise<{ data: TrackedOpportunity[] }> {
  return apiGet('/opportunities/tracked/list');
}

export function trackOpportunity(opportunityId: string): Promise<{
  tracked: boolean;
  opportunityId: string;
  trackedAt: string;
  notifications: string[];
}> {
  return apiPost(`/opportunities/${opportunityId}/track`);
}

export function untrackOpportunity(opportunityId: string): Promise<{ tracked: boolean; opportunityId: string }> {
  return apiDelete(`/opportunities/${opportunityId}/track`);
}
